import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row } from "@narmi/design_system";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { DateInput } from "@lending/components/DateInput/DateInput";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PhoneNumberInput } from "@lending/components/PhoneNumberInput/PhoneNumberInput";
import { TaxIDInput } from "@lending/components/TaxIDInput/TaxIDInput";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useGetApplicationApplicantDetailsSensitiveQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useGetApplicationApplicantDetailsSensitiveQuery";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { createSchema } from "@lending/hooks/queries/PersonalInfo/createSchema";
import { PersonalInfoFormData } from "@lending/hooks/queries/PersonalInfo/types";
import { useHandleUpdate } from "@lending/hooks/queries/PersonalInfo/useHandleUpdate";
import { useDeleteSessionQuery } from "@lending/hooks/queries/useDeleteSessionQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useFlowType } from "@lending/hooks/useFlowType";
import { useLendingCaptcha } from "@lending/hooks/useLendingCaptcha";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { getStepPath, useFlowNavigation } from "@lending/navigation";
import { GetStartedFields } from "@lending/types";
import { findPrimaryApplicant } from "@lending/utils/applicantHelpers";
import { InfoAlert } from "../components/InfoAlert";
import { useCreateAutoLoanApplication } from "./hooks/useCreateAutoLoanApplication";
import { useCreateCreditCardApplication } from "./hooks/useCreateCreditCardApplication";
import { useCreatePersonalLoanApplication } from "./hooks/useCreatePersonalLoanApplication";
import styles from "./PersonalInfo.module.css";
import { useStore } from "../../../contexts/StoreContext";

function PersonalInfo() {
  const { institution } = useGetInstitutionQuery();
  const { l10n } = useLocalization();
  const { goBack, goToPath, goToStep } = useFlowNavigation();
  const location = useLocation();
  const applicationUuid = useCurrentApplicationUuid();
  const flowType = useFlowType();
  const productType = useProductType();
  const getStartedState = location.state as GetStartedFields;
  const getStartedEmail = getStartedState?.email ?? "";
  const schema = createSchema(l10n, institution);
  const { showErrorToast } = useToastsContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { data: storeData, clearApplicationData } = useStore();
  const lendingApplicationID = applicationUuid ?? storeData.lendingApplicationID ?? "";
  const storeTaxId = storeData.tax_id;
  const storeDateOfBirth = storeData.date_of_birth;

  const { data: applicantResponse } = useListApplicationApplicantDetailsQuery();
  const existingApplicant = findPrimaryApplicant(applicantResponse?.results);
  const { data: sensitiveApplicant } = useGetApplicationApplicantDetailsSensitiveQuery(
    existingApplicant?.id ?? ""
  );
  const initialMaskedTaxId =
    storeTaxId && storeTaxId !== "" ? storeTaxId : (sensitiveApplicant?.tax_id ?? "");

  const isAutoLoan = productType === "auto-loan";
  const isPersonalLoan = productType === "personal-loan";

  const { handleUpdate } = useHandleUpdate();
  const { mutateAsync: deleteSession } = useDeleteSessionQuery();

  const autoLoanHandler = useCreateAutoLoanApplication();
  const creditCardHandler = useCreateCreditCardApplication();
  const personalLoanHandler = useCreatePersonalLoanApplication();
  const {
    captchaValue,
    handleRecaptchaError,
    recaptchaRef,
    validateCaptcha,
    setCaptchaValue,
    shouldShowCaptcha,
    siteKey,
  } = useLendingCaptcha();

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      first_name: existingApplicant?.first_name ?? "",
      last_name: existingApplicant?.last_name ?? "",
      tax_id: initialMaskedTaxId,
      date_of_birth: storeDateOfBirth ?? sensitiveApplicant?.date_of_birth ?? "",
      phone: existingApplicant?.phone ?? storeData.phone ?? "",
      email: existingApplicant?.email ?? storeData.email ?? getStartedEmail,
    },
    resolver: zodResolver(schema),
  });
  const handleApiError = (error: unknown) => {
    if (handleRecaptchaError(error)) {
      return;
    }

    let hasFieldErrors = false;
    if (error && typeof error === "object") {
      Object.entries(error).forEach(([key, value]) => {
        if (key in schema.shape && Array.isArray(value) && value.length > 0) {
          hasFieldErrors = true;
          setError(key as keyof PersonalInfoFormData, { message: (value as string[])[0] });
        }
      });
    }
    if (!hasFieldErrors) {
      showErrorToast(
        l10n.getString(
          "error-application-create",
          null,
          "There was an error creating your application. Please try again."
        )
      );
    }
  };

  const navigateToAddressStep = (createdApplicationId: string) => {
    if (productType) {
      const addressPath = getStepPath("address", productType, flowType, {}, createdApplicationId);

      if (addressPath) {
        goToPath(addressPath);
        return;
      }
    }

    goToStep("address");
  };

  useEffect(() => {
    if (existingApplicant) {
      const { first_name, last_name, email, phone } = existingApplicant;
      reset(
        {
          first_name: first_name ?? "",
          last_name: last_name ?? "",
          tax_id: initialMaskedTaxId,
          date_of_birth: storeDateOfBirth ?? sensitiveApplicant?.date_of_birth ?? "",
          phone: phone ?? "",
          email: email ?? getStartedEmail,
        },
        { keepDirtyValues: true }
      );
    }
  }, [
    existingApplicant,
    getStartedEmail,
    initialMaskedTaxId,
    reset,
    sensitiveApplicant,
    storeDateOfBirth,
  ]);

  async function onSubmit(data: PersonalInfoFormData) {
    if (!validateCaptcha()) {
      return;
    }

    setIsLoading(true);
    try {
      if (lendingApplicationID) {
        if (!existingApplicant) {
          return;
        }
        if (dirtyFields.tax_id || dirtyFields.date_of_birth) {
          clearApplicationData();
          await deleteSession();
          if (isPersonalLoan) {
            const { application: createdApplication } = await personalLoanHandler.createApplication(
              data,
              captchaValue
            );
            navigateToAddressStep(createdApplication.id);
          } else if (isAutoLoan) {
            const { application: createdApplication } = await autoLoanHandler.createApplication(
              data,
              captchaValue
            );
            navigateToAddressStep(createdApplication.id);
          } else {
            const result = await creditCardHandler.createApplication(data, captchaValue);
            if (!result) {
              return;
            }
            navigateToAddressStep(result.application.id);
          }
          return;
        }
        await handleUpdate(data, existingApplicant.id);
        return;
      }
      if (isPersonalLoan) {
        const { application: createdApplication } = await personalLoanHandler.createApplication(
          data,
          captchaValue
        );
        navigateToAddressStep(createdApplication.id);
      } else if (isAutoLoan) {
        const { application: createdApplication } = await autoLoanHandler.createApplication(
          data,
          captchaValue
        );
        navigateToAddressStep(createdApplication.id);
      } else {
        const result = await creditCardHandler.createApplication(data, captchaValue);
        if (!result) {
          return;
        }
        navigateToAddressStep(result.application.id);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <PageLayout>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "credit-card-personal-info-heading",
            null,
            "Enter your personal details"
          )}
          subheading={l10n.getString(
            "credit-card-personal-info-subheading",
            null,
            "We'll verify your identity and create your custom offer."
          )}
        />
        <div>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className={styles.formContent}>
              <TextInput
                name="first_name"
                control={control}
                label={l10n.getString("personal-info-first-name-label", null, "First name")}
              />
              <TextInput
                name="last_name"
                control={control}
                label={l10n.getString("personal-info-last-name-label", null, "Last name")}
              />
              <TaxIDInput
                name="tax_id"
                control={control}
                label={l10n.getString("personal-info-ssn-label", null, "Social Security Number")}
                isInitiallyMasked={Boolean(initialMaskedTaxId)}
                tooltipText={l10n.getString(
                  "ssn-tooltip-text",
                  null,
                  "We require this to prevent fraud and verify your identity."
                )}
              />
              <DateInput
                name="date_of_birth"
                control={control}
                label={l10n.getString("personal-info-dob-label", null, "Date of birth")}
              />
              {getStartedState?.email === undefined && (
                <TextInput
                  name="email"
                  control={control}
                  label={l10n.getString("personal-info-email-label", null, "Email")}
                  type="email"
                />
              )}
              <PhoneNumberInput
                name="phone"
                label={l10n.getString("personal-info-phone-label", null, "Phone number")}
                control={control}
              />
              <InfoAlert
                message={l10n.getString(
                  "welcome-screen-not-affect-credit-score",
                  null,
                  "Your application will not affect your credit score."
                )}
              />
              {shouldShowCaptcha && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={siteKey}
                  onChange={setCaptchaValue}
                  onExpired={() => setCaptchaValue(null)}
                />
              )}
            </div>
            <div>
              <Row alignItems="center" justifyContent="space-between">
                <BackButton onClick={goBack} />
                <Button
                  label={l10n.getString("button-label-next", null, "Next")}
                  type="submit"
                  isLoading={isLoading}
                />
              </Row>
            </div>
          </form>
        </div>
      </PageContent>
    </PageLayout>
  );
}

export { PersonalInfo };
